import React, { useState, useRef } from 'react';
import {
  Box,
  IconButton,
  Popover,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Divider,
  Autocomplete,
} from '@mui/material';
import { FilterList as FilterIcon, Clear as ClearIcon } from '@mui/icons-material';

export type FilterType = 'text' | 'date' | 'dateRange' | 'number' | 'numberRange' | 'select' | 'multiSelect' | 'autocomplete' | 'multiAutocomplete';

export interface SelectOption {
  value: string | number;
  label: string;
}

export interface ColumnFilterProps {
  type: FilterType;
  value: any;
  onChange: (value: any) => void;
  options?: SelectOption[];
  label?: string;
  placeholder?: string;
  color?: string;
  iconColor?: string;
}

const ColumnFilter: React.FC<ColumnFilterProps> = ({
  type,
  value,
  onChange,
  options = [],
  label,
  placeholder,
  color = '#9C27B0',
  iconColor,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [tempValue, setTempValue] = useState<any>(value);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setTempValue(value);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApply = () => {
    onChange(tempValue);
    handleClose();
  };

  const handleClear = () => {
    const emptyValue = getEmptyValue();
    setTempValue(emptyValue);
    onChange(emptyValue);
    handleClose();
  };

  const getEmptyValue = () => {
    switch (type) {
      case 'dateRange':
        return { from: '', to: '' };
      case 'numberRange':
        return { min: '', max: '' };
      case 'multiSelect':
      case 'multiAutocomplete':
        return [];
      default:
        return '';
    }
  };

  const hasValue = () => {
    if (value === null || value === undefined) return false;
    switch (type) {
      case 'dateRange':
        return value.from || value.to;
      case 'numberRange':
        return value.min !== '' || value.max !== '';
      case 'multiSelect':
      case 'multiAutocomplete':
        return Array.isArray(value) && value.length > 0;
      default:
        return value !== '';
    }
  };

  const open = Boolean(anchorEl);

  const renderFilterContent = () => {
    switch (type) {
      case 'text':
        return (
          <TextField
            size="small"
            fullWidth
            placeholder={placeholder || 'Rechercher...'}
            value={tempValue || ''}
            onChange={(e) => setTempValue(e.target.value)}
            autoFocus
          />
        );

      case 'date':
        return (
          <TextField
            size="small"
            fullWidth
            type="date"
            value={tempValue || ''}
            onChange={(e) => setTempValue(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        );

      case 'dateRange':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <TextField
              size="small"
              fullWidth
              type="date"
              label="Du"
              value={tempValue?.from || ''}
              onChange={(e) => setTempValue({ ...tempValue, from: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              fullWidth
              type="date"
              label="Au"
              value={tempValue?.to || ''}
              onChange={(e) => setTempValue({ ...tempValue, to: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        );

      case 'number':
        return (
          <TextField
            size="small"
            fullWidth
            type="number"
            placeholder={placeholder || 'Valeur...'}
            value={tempValue || ''}
            onChange={(e) => setTempValue(e.target.value)}
            autoFocus
          />
        );

      case 'numberRange':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <TextField
              size="small"
              fullWidth
              type="number"
              label="Min"
              value={tempValue?.min ?? ''}
              onChange={(e) => setTempValue({ ...tempValue, min: e.target.value })}
            />
            <TextField
              size="small"
              fullWidth
              type="number"
              label="Max"
              value={tempValue?.max ?? ''}
              onChange={(e) => setTempValue({ ...tempValue, max: e.target.value })}
            />
          </Box>
        );

      case 'select':
        return (
          <FormControl size="small" fullWidth>
            <InputLabel>{label || 'Sélectionner'}</InputLabel>
            <Select
              value={tempValue || ''}
              label={label || 'Sélectionner'}
              onChange={(e) => setTempValue(e.target.value)}
            >
              <MenuItem value="">
                <em>Tous</em>
              </MenuItem>
              {options.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'autocomplete':
        return (
          <Autocomplete
            size="small"
            fullWidth
            options={options}
            getOptionLabel={(opt) => opt.label}
            value={options.find((opt) => opt.value === tempValue) || null}
            onChange={(_, newVal) => setTempValue(newVal ? newVal.value : '')}
            renderInput={(params) => (
              <TextField {...params} label={label || 'Rechercher...'} placeholder="Tapez pour rechercher..." />
            )}
            isOptionEqualToValue={(opt, val) => opt.value === val.value}
            noOptionsText="Aucun résultat"
            sx={{ minWidth: 220 }}
          />
        );

      case 'multiSelect':
        return (
          <FormControl size="small" fullWidth>
            <InputLabel>{label || 'Sélectionner'}</InputLabel>
            <Select
              multiple
              value={tempValue || []}
              label={label || 'Sélectionner'}
              onChange={(e) => setTempValue(e.target.value)}
              renderValue={(selected) => {
                const selectedLabels = (selected as (string | number)[])
                  .map((val) => options.find((opt) => opt.value === val)?.label || val)
                  .join(', ');
                return selectedLabels;
              }}
            >
              {options.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  <Checkbox checked={(tempValue || []).includes(opt.value)} />
                  <ListItemText primary={opt.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'multiAutocomplete': {
        const selectedValues: (string | number)[] = Array.isArray(tempValue) ? tempValue : [];
        const selectedOptions = options.filter((opt) => selectedValues.includes(opt.value));
        return (
          <Autocomplete
            multiple
            size="small"
            fullWidth
            options={options}
            getOptionLabel={(opt) => opt.label}
            value={selectedOptions}
            onChange={(_, newVals) => setTempValue(newVals.map((v) => v.value))}
            renderInput={(params) => (
              <TextField {...params} label={label || 'Rechercher...'} placeholder="Tapez pour rechercher..." />
            )}
            isOptionEqualToValue={(opt, val) => opt.value === val.value}
            disableCloseOnSelect
            noOptionsText="Aucun résultat"
            sx={{ minWidth: 260 }}
          />
        );
      }

      default:
        return null;
    }
  };

  return (
    <>
      <IconButton
        ref={buttonRef}
        size="small"
        onClick={handleOpen}
        sx={{
          ml: 0.5,
          p: 0.25,
          color: iconColor ? iconColor : (hasValue() ? color : 'inherit'),
          opacity: hasValue() ? 1 : (iconColor ? 0.75 : 0.5),
          '&:hover': {
            opacity: 1,
            color: iconColor || color,
            backgroundColor: iconColor ? 'rgba(255,255,255,0.15)' : undefined,
          },
        }}
      >
        <FilterIcon sx={{ fontSize: 18 }} />
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, minWidth: 200, maxWidth: 280 }}>
          {label && (
            <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary' }}>
              Filtrer par {label}
            </Typography>
          )}

          {renderFilterContent()}

          <Divider sx={{ my: 1.5 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <Button
              size="small"
              startIcon={<ClearIcon />}
              onClick={handleClear}
              sx={{ color: 'text.secondary' }}
            >
              Effacer
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleApply}
              sx={{ bgcolor: color, '&:hover': { bgcolor: color, filter: 'brightness(0.9)' } }}
            >
              Appliquer
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default ColumnFilter;
